<script setup lang="ts">
  // Vue core imports
  import type { PropType } from 'vue'
  import { computed, onMounted, ref, watch } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { ArrowLeftIcon, Mail01Icon, XCloseIcon } from '@gohighlevel/ghl-icons/24/outline'

  import CPAvatar from '@ui/components/CPAvatar.vue'
  import CPButton from '@ui/components/CPButton.vue'
  import CPModal from '@ui/components/CPModal.vue'
  import CPText from '@ui/components/CPText.vue'

  // Internal imports
  import { createElementId } from '../../core-layer/utils'
  import { useLocation } from '../../location-layer/composables/useLocation'
  import { useAuthStore } from '../store/auth.store'
  import { LoginEvents, Screens } from '../types'
  import AuthHandler from './AuthHandler.vue'

  // Type imports

  // Props definitions
  const props = defineProps({
    referrerUser: {
      type: Object as PropType<{
        fullName: string
        avatar?: string
        email?: string
        name?: string
        _id?: string
      }>,
      required: false,
      default: undefined,
    },
    brandLogo: {
      type: String as PropType<string | null>,
      required: false,
      default: null,
    },
    brandName: {
      type: String,
      required: false,
      default: '',
    },
    isSsoDisabled: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    deferSetPasswordSession: {
      type: Boolean,
      required: false,
      default: false,
    },
  })

  // Emits definitions
  const emit = defineEmits(['loginSuccess'])

  const { t } = useI18n()

  // Refs and reactive variables
  const authStore = useAuthStore()
  const { store: locationStore } = useLocation()
  const isLoading = ref(false)
  const currentScreen = ref('')
  const navigationHistory = ref([] as string[])
  const currentUserEmail = ref('')

  // Computed properties
  const visible = computed({
    get: () => authStore.showLoginModal,
    set: (val: boolean) => {
      if (!val) {
        authStore.closeLoginModal()
      }
    },
  })

  const page = computed(() => authStore.modalPage)

  const modalWidth = computed(() => {
    return currentScreen.value === Screens.VERIFY_SECURE_CODE ? 425 : 452
  })

  const resolvedIsSsoDisabled = computed(() => {
    if (props.isSsoDisabled !== undefined) return props.isSsoDisabled
    return locationStore.locationData?.isSSODisabled === true
  })

  const defaultLoginScreen = computed(() => {
    return locationStore.locationData?.defaultLoginPage === 'SECURE_CODE' ? Screens.LOGIN_WITH_SECURE_CODE : Screens.LOGIN
  })

  // Lifecycle hooks
  onMounted(async () => {
    // Initialize navigation history
    const initialPage =
      authStore.modalPage === Screens.LOGIN && locationStore.locationData?.defaultLoginPage === 'SECURE_CODE'
        ? Screens.LOGIN_WITH_SECURE_CODE
        : authStore.modalPage
    if (initialPage !== authStore.modalPage) {
      authStore.setModalPage(initialPage)
    }
    navigationHistory.value.push(initialPage)
  })

  // Watch effects
  watch(
    () => authStore.modalPage,
    (newVal: string) => {
      if (newVal !== navigationHistory.value[navigationHistory.value.length - 1]) {
        navigationHistory.value.push(newVal)
      }
    }
  )

  // User-defined functions
  interface ScreenData {
    page: string
    email?: string
    username?: string
    password?: string
    otpToken?: string
    otp?: string
    action?: string
    referrerUser?: {
      email?: string
      name?: string
      _id?: string
    }
    passwordResetToken?: string
    contactId?: string
  }

  async function handleChangeScreen(screen: ScreenData) {
    currentScreen.value = screen.page

    if (screen.page === Screens.LOGIN || screen.page === Screens.LOGIN_WITH_SECURE_CODE) {
      navigationHistory.value = [screen.page]
    } else {
      if (navigationHistory.value.length === 0 || navigationHistory.value[navigationHistory.value.length - 1] !== screen.page) {
        navigationHistory.value.push(screen.page)
      }
    }

    authStore.setModalPage(screen.page)
    currentUserEmail.value = screen.email || ''
  }

  interface LoginEvent {
    eventType: string
    token?: string
    user?: {
      id?: string
      email?: string
      fullName?: string
    }
  }

  async function loginCallback(event: LoginEvent) {
    if (event.eventType == LoginEvents.LOGIN_SUCCESSFUL) {
      isLoading.value = true
      window.localStorage.removeItem('refCode')
      emit('loginSuccess')
      isLoading.value = false
      visible.value = false
    }
  }

  function handleBack() {
    navigationHistory.value.pop()
    const previousScreen = navigationHistory.value[navigationHistory.value.length - 1] || defaultLoginScreen.value
    authStore.setModalPage(previousScreen)
    currentScreen.value = previousScreen
  }
</script>

<template>
  <CPModal
    :id="createElementId('login', 'signup', 'modal')"
    :show="visible"
    :width="modalWidth"
    :show-header-icon="false"
    :show-footer="false"
    :show-close="false"
    :show-back="false"
    :show-icon="false"
    @update:show="visible = $event"
  >
    <template #header>
      <div
        v-if="currentScreen === Screens.LOGIN_WITH_SECURE_CODE"
        class="mx-auto flex w-[26.25rem] items-center justify-between max-sm:w-full"
      >
        <div class="flex items-center justify-start gap-2">
          <div :id="createElementId('login-signup', 'modal', 'back-button')" @click="handleBack">
            <ArrowLeftIcon class="h-5 w-5 cursor-pointer rounded-full hover:bg-hover" />
          </div>
          <CPText size="xl" weight="semibold" class="text-body">
            {{ t('auth.loginModal.enterEmailToLogin') }}
          </CPText>
        </div>
      </div>
      <div
        v-else-if="currentScreen === Screens.FORGOT_PASSWORD"
        class="mx-auto flex w-[26.25rem] items-center justify-between max-sm:w-full"
      >
        <div class="flex items-center justify-start gap-2">
          <div :id="createElementId('login-signup', 'modal', 'back-button')" @click="handleBack">
            <ArrowLeftIcon class="h-5 w-5 cursor-pointer rounded-full hover:bg-hover" />
          </div>
          <CPText size="xl" weight="semibold" class="text-body">
            {{ t('auth.loginModal.resetYourPassword') }}
          </CPText>
        </div>
      </div>
      <div
        v-else-if="currentScreen === Screens.VERIFY_SECURE_CODE"
        class="mx-auto flex w-[24.75rem] items-center justify-between max-sm:w-full"
      >
        <div class="flex w-full min-w-0 items-center justify-start gap-2">
          <div :id="createElementId('login-signup', 'modal', 'back-button')" @click="handleBack">
            <ArrowLeftIcon class="h-5 w-5 flex-shrink-0 cursor-pointer rounded-full hover:bg-hover" />
          </div>
          <div class="flex w-full min-w-0 items-center gap-2">
            <Mail01Icon class="h-6 w-6 flex-shrink-0" />
            <div class="flex w-full min-w-0 flex-col">
              <CPText size="xl" weight="semibold" class="text-body">
                {{ t('auth.loginModal.verifySecureCode') }}
              </CPText>
              <CPText size="sm" weight="regular" class="truncate text-secondary max-sm:whitespace-normal max-sm:break-words">
                {{ t('auth.loginModal.sentTo', { email: currentUserEmail }) }}
              </CPText>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="flex w-[26.25rem] items-center justify-between max-sm:w-full">
        <div class="flex items-center gap-2">
          <CPAvatar v-if="brandLogo" :src="brandLogo" :imgProps="{ alt: brandName || 'Logo' }" :round="false" size="sm" />
          <CPText v-if="brandName" size="3xl" weight="bold" class="text-body">
            {{ brandName }}
          </CPText>
        </div>
        <CPButton
          :id="createElementId('login-signup', 'modal', 'close-button')"
          type="text"
          :color="'transparent'"
          @click="visible = false"
        >
          <XCloseIcon class="w-6 h-6" />
        </CPButton>
      </div>
    </template>
    <div class="flex w-full flex-col items-center justify-center">
      <div v-if="referrerUser?.fullName" class="mb-4">
        <CPText size="md" weight="regular" class="flex items-center justify-center px-6">
          {{ t('auth.loginModal.youWereReferredBy') }}
          <CPAvatar
            v-if="referrerUser?.avatar"
            :src="referrerUser.avatar"
            :alt="referrerUser.fullName || 'User avatar'"
            class="ml-2"
            size="sm"
          />
          <CPAvatar v-else class="ml-2" size="sm">
            {{
              referrerUser.fullName.includes(' ')
                ? referrerUser.fullName.split(' ')[0].charAt(0).toUpperCase() + referrerUser.fullName.split(' ')[1].charAt(0).toUpperCase()
                : referrerUser.fullName.charAt(0).toUpperCase()
            }}
          </CPAvatar>
          <span class="ml-2">{{ referrerUser.fullName }}</span>
        </CPText>
      </div>

      <!-- show-branding false: modal header already shows portal logo+name for WL; avoid duplicate in form body -->
      <AuthHandler
        :page="page"
        :referrer-user="
          referrerUser
            ? {
                email: referrerUser.email || '',
                name: referrerUser.fullName,
                _id: referrerUser._id,
              }
            : undefined
        "
        :show-branding="false"
        :is-title-enabled="false"
        :is-sso-disabled="resolvedIsSsoDisabled"
        :defer-set-password-session="props.deferSetPasswordSession"
        @login-callback="loginCallback"
        @change-screen="handleChangeScreen"
      />
    </div>
  </CPModal>
</template>

<style scoped>
  :deep(.hr-dialog__title .hr-text.hr-text-2xl.hr-text-semibold) {
    width: 100%;
  }
</style>
